import { makeAutoObservable } from 'mobx';
import createStoreContext from '../createStoreContext';
import {
  connectConnector,
  disconnectConnector,
  generateUniqueApiKey,
  getConnectedConnectorsInOrg,
} from 'src/services/connector';
import {
  ConnectConnectorBody,
  ConnectorCardField,
  DisconnectConnectorBody,
} from 'src/types/connector';
import {
  addImagesInConnectorsResponse,
  findConnectorDetailsByType,
} from 'src/utils/connectors';
import { CONNECTOR_NAME, CONNECTOR_VARIANT } from 'src/constants/connectors';

export const [ConnectorsStoreProvider, useConnectorsStore] = createStoreContext(
  class ConnectorsStore {
    private _connectedConnectorCards: Array<ConnectorCardField> = [];

    constructor() {
      makeAutoObservable(this);
    }

    get connectedConnectorCards() {
      return this._connectedConnectorCards;
    }

    fetchConnectedConnectorsDetailsInOrg = async (
      variant?: CONNECTOR_VARIANT,
    ): Promise<ConnectorCardField[]> => {
      const {
        success: getConnectedConnectorsInOrgSuccess,
        content: connectedConnectorsInOrg,
      } = await getConnectedConnectorsInOrg(variant);

      if (!getConnectedConnectorsInOrgSuccess || !connectedConnectorsInOrg) {
        this._connectedConnectorCards = [];

        return [];
      }

      const withImages = addImagesInConnectorsResponse(
        connectedConnectorsInOrg,
      );

      this._connectedConnectorCards = withImages;

      return withImages;
    };

    connectConnector = async (body: ConnectConnectorBody): Promise<boolean> => {
      const { success } = await connectConnector(body);

      if (success) {
        return true;
      }

      return false;
    };

    disconnectConnector = async (
      body: DisconnectConnectorBody,
    ): Promise<string | null> => {
      const { success, content } = await disconnectConnector(body);

      if (success && content) {
        return content;
      }

      return null;
    };

    generateUniqueApiKey = async (): Promise<string | null> => {
      const { success, content } = await generateUniqueApiKey();

      if (success && content) {
        return content;
      }

      return null;
    };

    updateConnectorDetailsByType = (
      updatedConnectorCardField: ConnectorCardField,
    ): ConnectorCardField | undefined => {
      const connectorIndex = this._connectedConnectorCards.findIndex(
        (connector) => connector.type === updatedConnectorCardField.type,
      );

      if (connectorIndex === -1) {
        return;
      }

      const updatedConnector = {
        ...this._connectedConnectorCards[connectorIndex],
        ...updatedConnectorCardField,
      };

      this._connectedConnectorCards[connectorIndex] = updatedConnector;

      return updatedConnector;
    };

    checkIfConnectorConnected = async (connectorType: CONNECTOR_NAME) => {
      if (
        this._connectedConnectorCards &&
        this._connectedConnectorCards.length > 0
      ) {
        const connectorDetails = findConnectorDetailsByType(
          connectorType,
          this._connectedConnectorCards,
        );

        if (!connectorDetails) {
          return false;
        }

        return connectorDetails.connectedStatus;
      }

      // We dont have connector cards, we need to get them

      const cards = await this.fetchConnectedConnectorsDetailsInOrg();

      this._connectedConnectorCards = cards;

      const connectorDetails = findConnectorDetailsByType(
        connectorType,
        this._connectedConnectorCards,
      );

      if (!connectorDetails) {
        return false;
      }

      return connectorDetails.connectedStatus;
    };
  },
);

export type ConnectorsStore = ReturnType<typeof useConnectorsStore>;
